import React, { useEffect, useState, useMemo } from "react";
import image from "../../../assets/user-image.png";
import { useProfile } from "../useHook";
import Swal from "sweetalert2";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Loader from "../../../Utils/loader";

const UserInfo = () => {
  const { getProfile, updatePassword } = useProfile();
  const [getUserData, setUserData] = useState();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await getProfile(setUserData);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      Swal.fire({
        title: "Error",
        text: "Passwords do not match!",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const body = {
      currentPassword,
      newPassword,
    };

    try {
      setIsSubmitting(true);
      const response = await updatePassword(body);

      if (response?.status === 200) {
        Swal.fire({
          title: "Success",
          text: "Your password has been updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        resetPasswordForm();
      } else {
        Swal.fire({
          title: "Error",
          text: response?.data?.message || "Failed to update password.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const userRoles = useMemo(() => {
    const formatRole = (roleName) => {
      if (!roleName) return "";
      return roleName
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    if (getUserData?.assignedRoles && Array.isArray(getUserData.assignedRoles)) {
      return getUserData.assignedRoles
        .map((role) => formatRole(role.name))
        .join(", ");
    }
    return formatRole(getUserData?.role) || "Software Engineer";
  }, [getUserData]);

  if (loading) {
    return <Loader message="Loading profile..." />;
  }

  return (
    <div>
      <div className="bg-gray-100 p-4 flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Profile</h2>
      </div>

      <div className="bg-white p-6 rounded-lg pagination-shadow mb-6">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex-shrink-0">
            <img
              className="w-28 h-28 rounded-full border-2 border-gray-200 object-cover"
              src={getUserData?.profileImage || image}
              alt="Profile"
            />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-gray-800">
              {getUserData?.firstName || ""} {getUserData?.lastName || ""}
            </h3>
            <p className="text-gray-600 capitalize mt-1">{userRoles}</p>
            <div className="mt-4 space-y-2 text-sm">
              <p className="text-gray-700">
                Email:{" "}
                <span className="font-medium text-gray-900">
                  {getUserData?.email || "N/A"}
                </span>
              </p>
              <p className="text-gray-700">
                Phone:{" "}
                <span className="font-medium text-gray-900">
                  {getUserData?.contactNo || "N/A"}
                </span>
              </p>
              <p className="text-gray-700">
                User Role:{" "}
                <span className="font-medium text-gray-900 capitalize">
                  {userRoles}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg pagination-shadow max-w-2xl">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Update Password
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Current Password
            </label>
            <input
              type={showCurrentPassword ? "text" : "password"}
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md"
              placeholder="Enter current password"
              required
            />
            <button
              type="button"
              className="absolute top-9 text-xl right-3 cursor-pointer text-gray-500"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            >
              {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="relative">
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New Password
            </label>
            <input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md"
              placeholder="Enter new password"
              required
            />
            <button
              type="button"
              className="absolute top-9 text-xl right-3 cursor-pointer text-gray-500"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="relative">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm New Password
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md"
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              className="absolute top-9 text-xl right-3 cursor-pointer text-gray-500"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetPasswordForm}
              className="btn btn-reset-filter justify-center"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn btn-edit text-white justify-center ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserInfo;
